import '@fontsource/tektur/600.css';
import '@fontsource/tektur/800.css';
import { Game } from '@/game';
import { t } from '@/i18n';
import { hookFullscreenRetry } from '@/core/fullscreen';

/**
 * Entry point. Pixi + game setup live in Game.create().
 * Fullscreen is opt-in (in-game prompt + settings) and only re-enters on
 * pointer gestures — keydown never triggers it (Esc must stay usable).
 */
/** Portrait phones get a ROTATE overlay before anything else (docs/12 O3). */
function hookOrientationGuard(): void {
  const coarse = window.matchMedia?.('(pointer: coarse)').matches || 'ontouchstart' in window;
  if (!coarse) return;
  const el = document.createElement('div');
  el.style.cssText =
    'position:fixed;inset:0;background:#0a0614;color:#45d9c5;display:none;' +
    'align-items:center;justify-content:center;z-index:99;text-align:center;' +
    'font-family:Tektur,sans-serif;font-size:22px;padding:24px';
  el.textContent = t('rotate.title');
  document.body.appendChild(el);
  const update = (): void => {
    el.style.display = window.innerHeight > window.innerWidth ? 'flex' : 'none';
  };
  window.addEventListener('resize', update);
  update();
}

async function boot(): Promise<void> {
  hookFullscreenRetry();
  hookOrientationGuard();
  // Xbox Edge maps the controller to a VIRTUAL MOUSE by default — ask for
  // raw gamepad input where the hook exists, and drop the cursor there
  const nav = navigator as Navigator & { gamepadInputEmulation?: string };
  if (typeof nav.gamepadInputEmulation === 'string') {
    nav.gamepadInputEmulation = 'gamepad';
  }
  if (/Xbox/i.test(navigator.userAgent)) {
    document.body.style.cursor = 'none';
  }
  // Pixi rasterizes text once and canvas text never triggers CSS @font-face
  // fetching — fonts.ready alone resolves BEFORE Tektur exists (docs/12 I4).
  // Explicit load() calls force the fetch; the glyph sample pulls latin-ext.
  try {
    await Promise.all([
      document.fonts.load('600 16px Tektur', 'ATLAS BOSS RUSH ŞĞİıçü'),
      document.fonts.load('800 16px Tektur', 'ATLAS BOSS RUSH ŞĞİıçü'),
    ]);
  } catch {
    /* font fetch failed — system fallback still works */
  }
  await document.fonts.ready;
  const game = await Game.create();
  // Insurance for exotic GPUs: if anything blows up at render time, the
  // first suspect is the ambiance shader — strip filters and keep playing
  window.addEventListener('error', () => game.disableFilters(), { once: true });
  game.start();
}

void boot();
